import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface DeviceInfo {
  deviceType: 'mobile' | 'tablet' | 'desktop';
  operatingSystem: string;
  browser: string;
  screenWidth: number;
  screenHeight: number;
  language: string;
  timezone: string;
  userAgent: string;
}

interface LocationInfo {
  country?: string;
  hashedId?: string;
}

// Generate unique session ID
const generateSessionId = () => {
  return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

// Detect device information
const getDeviceInfo = (): DeviceInfo => {
  const userAgent = navigator.userAgent;
  
  // Detect device type
  let deviceType: 'mobile' | 'tablet' | 'desktop' = 'desktop';
  if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(userAgent)) {
    deviceType = 'mobile';
  } else if (/Tablet|iPad/.test(userAgent)) {
    deviceType = 'tablet';
  }

  // Detect OS
  let operatingSystem = 'Unknown';
  if (userAgent.includes('Windows')) operatingSystem = 'Windows';
  else if (userAgent.includes('Mac')) operatingSystem = 'macOS';
  else if (userAgent.includes('Linux')) operatingSystem = 'Linux';
  else if (userAgent.includes('Android')) operatingSystem = 'Android';
  else if (userAgent.includes('iOS') || userAgent.includes('iPhone') || userAgent.includes('iPad')) operatingSystem = 'iOS';

  // Detect browser
  let browser = 'Unknown';
  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) browser = 'Chrome';
  else if (userAgent.includes('Firefox')) browser = 'Firefox';
  else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) browser = 'Safari';
  else if (userAgent.includes('Edg')) browser = 'Edge';
  else if (userAgent.includes('Opera')) browser = 'Opera';

  return {
    deviceType,
    operatingSystem,
    browser,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    userAgent
  };
};

// Create a privacy-safe hash based on non-sensitive browser data
const createPrivacyHash = async (): Promise<string> => {
  try {
    const data = [
      navigator.language,
      navigator.platform,
      screen.width.toString(),
      screen.height.toString(),
      new Date().toDateString(), // Daily rotation for privacy
      'privacy_salt_2024'
    ].join('|');
    
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    
    // Return only first 12 characters for privacy
    return hashHex.substring(0, 12);
  } catch (error) {
    // Fallback for older browsers
    return Math.random().toString(36).substring(2, 14);
  }
};

// Get basic location info (country only, privacy-focused)
const getLocationInfo = async (): Promise<LocationInfo> => {
  try {
    // Privacy-focused approach - only basic geo info from timezone
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timeZoneCountryMap: { [key: string]: string } = {
      'Europe/Stockholm': 'Sweden',
      'Europe/London': 'United Kingdom',
      'America/New_York': 'United States',
      'America/Los_Angeles': 'United States',
      'Europe/Berlin': 'Germany',
      'Europe/Paris': 'France',
      'Europe/Madrid': 'Spain',
      'Europe/Rome': 'Italy',
      'Europe/Amsterdam': 'Netherlands',
      'Asia/Tokyo': 'Japan',
      'Australia/Sydney': 'Australia',
      // Add more as needed, but keep it basic
    };
    
    const country = timeZoneCountryMap[timeZone] || 'Other';
    const hashedId = await createPrivacyHash();
    
    return {
      country,
      hashedId
    };
  } catch (error) {
    console.log('Could not get location info:', error);
    return {};
  }
};

export const useAnalytics = () => {
  const [sessionId] = useState(() => generateSessionId());
  const [deviceInfo] = useState(() => getDeviceInfo());
  const [locationInfo, setLocationInfo] = useState<LocationInfo>({});
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize analytics - completely non-blocking for initial page load
  useEffect(() => {
    const initAnalytics = async () => {
      const location = await getLocationInfo();
      setLocationInfo(location);
      setIsInitialized(true);

      // Only start analytics after page is fully loaded to avoid blocking critical path
      const startAnalytics = () => {
        setTimeout(async () => {
          try {
            await supabase.from('user_analytics').insert({
              session_id: sessionId,
              device_type: deviceInfo.deviceType,
              operating_system: deviceInfo.operatingSystem,
              browser: deviceInfo.browser,
              screen_width: deviceInfo.screenWidth,
              screen_height: deviceInfo.screenHeight,
              country: location.country,
              hashed_ip: location.hashedId,
              user_agent: deviceInfo.userAgent,
              language: deviceInfo.language,
              timezone: deviceInfo.timezone
            });
          } catch (error) {
            console.error('Error storing user analytics:', error);
          }
        }, 2000); // Wait 2 seconds after page load
      };

      // Wait for page to be fully loaded before starting analytics
      if (document.readyState === 'complete') {
        startAnalytics();
      } else {
        window.addEventListener('load', startAnalytics, { once: true });
      }
    };

    // Use requestIdleCallback with a significant delay to ensure no blocking
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => initAnalytics(), { timeout: 5000 });
    } else {
      setTimeout(initAnalytics, 1000);
    }
  }, [sessionId, deviceInfo]);

  // Track song play
  const trackSongPlay = async (
    songTitle: string,
    songArtist: string,
    songSrc: string,
    playDuration: number,
    completed: boolean = false
  ) => {
    if (!isInitialized) return;

    try {
      await supabase.from('song_play_analytics').insert({
        song_title: songTitle,
        song_artist: songArtist,
        song_src: songSrc,
        session_id: sessionId,
        device_type: deviceInfo.deviceType,
        operating_system: deviceInfo.operatingSystem,
        browser: deviceInfo.browser,
        country: locationInfo.country,
        play_duration: playDuration,
        completed
      });
    } catch (error) {
      console.error('Error tracking song play:', error);
    }
  };

  // Track artwork view
  const trackArtworkView = async (
    artworkName: string,
    viewDuration: number = 0
  ) => {
    if (!isInitialized) return;

    try {
      await supabase.from('artwork_views').insert({
        session_id: sessionId,
        artwork_name: artworkName,
        view_duration: viewDuration,
        device_type: deviceInfo.deviceType,
        country: locationInfo.country
      });
    } catch (error) {
      console.error('Error tracking artwork view:', error);
    }
  };

  // Track page view
  const trackPageView = async (
    pagePath: string,
    referrer?: string
  ) => {
    if (!isInitialized) return;

    try {
      await supabase.from('page_views').insert({
        session_id: sessionId,
        page_path: pagePath,
        referrer: referrer || document.referrer,
        device_type: deviceInfo.deviceType,
        country: locationInfo.country,
        browser: deviceInfo.browser,
        operating_system: deviceInfo.operatingSystem
      });
    } catch (error) {
      console.error('Error tracking page view:', error);
    }
  };

  // Get analytics summary using privacy-safe aggregated data only
  const getAnalyticsSummary = async () => {
    try {
      // Use privacy-safe analytics function
      const { data: privacySafeStats } = await supabase
        .rpc('get_privacy_safe_analytics');

      // Get song statistics (already privacy-safe with thresholds)
      const { data: songStats } = await supabase
        .rpc('get_song_statistics');

      // Get artwork statistics
      const { data: artworkStats } = await supabase
        .rpc('get_top_artworks');

      return {
        summary: privacySafeStats?.[0] || null,
        songStats,
        artworkStats
      };
    } catch (error) {
      console.error('Error getting analytics summary:', error);
      return null;
    }
  };

  return {
    sessionId,
    deviceInfo,
    locationInfo,
    isInitialized,
    trackSongPlay,
    trackArtworkView,
    trackPageView,
    getAnalyticsSummary
  };
};
